import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {SelectEditor} from "../../../../misc/scroller/SelectEditor";
import React, {useContext, useState} from "react";
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {useAsyncAction} from "nate-react-api-helpers";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {Opening} from "../../../../api/Openings";
import {DoorElevationInner, DoorElevationPopup} from "./DoorElevationPopup";
import {TabWrapper} from "../../shopdrawing/TabWrapper";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {
    addThermalPrefix, canHaveThermal,
    hasThermalPrefix,
    legacyDoorElevationSuffix, renderDoorElevationStr, stripDoorElevationPrefix
} from "../../shopdrawing/openings/DoorElevationHelpers";
import {DropDownContext, DropdownDataLoading, useDropdownData} from "../../settings/DropDowns";

export function DoorElevationSelector<T extends (QuoteOpening | Opening)>(props: {
    anchor: any;
    row: T;
    quoteMode?: boolean;
    width: number;
    initialValue: string;
    onCancel(): void;
    onDone(value: T): Promise<any>;
}) {
    const [value, setValue] = useState(renderDoorElevationStr(props.row, true));
    const [initialValue] = useState(renderDoorElevationStr(props.row, true));
    const [initialValueRaw] = useState(renderDoorElevationStr(props.row, false));
    const [search, setSearch] = useState("");
    const [showCustom, setShowCustom] = useState<T|null>(null);
    const editCtx = useContext(EditContext);
    const ddlCtx = useContext(DropDownContext);
    const ddlData = useDropdownData(ddlCtx.doorElevations);
    const opts = ddlData.map(o => ({...o, display: o.display + legacyDoorElevationSuffix}));

    const baseOpts = ddlData.map(o => o);
    if(props.row.openingType === "Pair") {
        opts.push({value: "-", display: "Split/Custom"} as any);
    }

    if(ddlCtx.loading) {
        return <DropdownDataLoading anchor={props.anchor} width={props.width} />
    }

    const hasSplit = props.row.inactiveDoorElevation !== "" && props.row.doorElevation !== props.row.inactiveDoorElevation;

    if(value === "-" || hasSplit) {
        return <SplitElevations<T> onDone={props.onDone} onCancel={props.onCancel} row={props.row} />
    }

    const isCustomOption = value !== "" && !opts.some(o => o.value === value);
    if(!props.quoteMode || isCustomOption) {
        return (<DoorElevationPopup row={props.row}
                                    onCancel={props.onCancel} onDone={props.onDone} />);
    }

    if(showCustom !== null) {
        return (<DoorElevationPopup row={showCustom}
                                    onCancel={props.onCancel} onDone={props.onDone} />);
    }

    type ElevationExt = {
        value: string;
        display: string;
        doorElevation: string;
        doorElevationFile?: number;
        inactiveDoorElevation: string;
        inactiveDoorElevationFile?: number
    }

    const elevations: ElevationExt[] = [];
    const customSuffix = ": Custom"
    editCtx.dataRef.current.map(d => {

        d.rows.map(r => {
            const op = r as (Opening | QuoteOpening);
            if(op.doorElevation === "") return;

            let name = renderDoorElevationStr(op);
            if(opts.some(v => v.value === name)) return;

            name = name + customSuffix;
            if(elevations.some(v => v.display === name)) return;

            elevations.push({
                value: name,
                display: name,
                doorElevation: op.doorElevation,
                doorElevationFile: op.doorElevationFile,
                inactiveDoorElevation: op.inactiveDoorElevation,
                inactiveDoorElevationFile: op.inactiveDoorElevationFile
            });
        })
    });

    opts.push(...elevations.filter(e => {
        if(!hasThermalPrefix(e.value)) return true; // standard & non-thermal customs show

        const base = stripDoorElevationPrefix(e.value, true).replace(customSuffix, "");
        const isBaseOptWithThermal = baseOpts.some(b => b.value === base);
        return !isBaseOptWithThermal; // include custom thermal options
    }) as any)

    return (
        <SelectEditor
            anchor={props.anchor}
            options={opts}
            value={value}
            initialValue={initialValue}
            onBlur={props.onCancel}
            onSearch={setSearch}
            freeSolo={!baseOpts.some(o => o.value.toLowerCase() === search.toLowerCase().trim())}
            onSelect={(value, freeSolo) => {
                if(value === "-")  {
                    setValue(value);
                    return;
                }

                let setThermal = initialValue === "" && props.row.exterior && canHaveThermal(value, opts);
                if(hasThermalPrefix(initialValueRaw) && canHaveThermal(value, opts)) {
                    setThermal = true;
                }

                const obj = opts.find(o => o.value === value);
                if(obj && "doorElevation" in obj) {
                    const obj2 = obj as any;

                    if(obj2.doorElevationFile) {
                        props.onDone(Object.assign({}, props.row, {
                            doorElevation: addThermalPrefix(obj2.doorElevation, setThermal),
                            doorElevationFile: obj2.doorElevationFile,
                            inactiveDoorElevation: addThermalPrefix(obj2.inactiveDoorElevation, setThermal),
                            inactiveDoorElevationFile: obj2.inactiveDoorElevationFile,
                        }) as any);

                        return;
                    }

                    setShowCustom(Object.assign({}, props.row, {
                        doorElevation: addThermalPrefix(obj2.doorElevation, setThermal),
                        doorElevationFile: obj2.doorElevationFile,
                        inactiveDoorElevation: addThermalPrefix(obj2.inactiveDoorElevation, setThermal),
                        inactiveDoorElevationFile: obj2.inactiveDoorElevationFile,
                    }) as any);

                    return;
                }

                if(freeSolo) {
                    setShowCustom(Object.assign({}, props.row, {
                        doorElevation: addThermalPrefix(value, setThermal),
                        doorElevationFile: null,
                        inactiveDoorElevation: "",
                        inactiveDoorElevationFile: null,
                    }) as any);

                    return;
                }

                props.onDone(Object.assign({}, props.row, {
                    doorElevation: addThermalPrefix(value, setThermal),
                    doorElevationFile: null,
                    inactiveDoorElevation: "",
                    inactiveDoorElevationFile: null,
                }));
            }} />
    )
}

function SplitElevations<T extends (QuoteOpening | Opening)>(props: {
    row: T;
    onDone(value: T): Promise<any>;
    onCancel(): void;
}) {
    const updaterRef = useSyncedRef(props.onDone);
    const updater = useAsyncAction((input: T) => {
        return updaterRef.current(input)
    }, []);

    const [activeElevation, setActiveElevation] = useState(props.row.doorElevation as string);
    // @ts-ignore
    const [activeElevationFile, setActiveElevationFile] = useState<number|undefined>(props.row.doorElevationFile || undefined);
    // @ts-ignore
    const [activeElevationFileName, setActiveElevationFileName] = useState<string|undefined>(props.row.doorElevationFileName || undefined);

    const [inactiveElevation, setInactiveElevation] = useState<string>(props.row.inactiveDoorElevation as string);
    // @ts-ignore
    const [inactiveElevationFile, setInactiveElevationFile] = useState<number|undefined>(props.row.inactiveDoorElevationFile || undefined);
    // @ts-ignore
    const [inactiveElevationFileName, setInactiveElevationFileName] = useState<string|undefined>(props.row.inactiveDoorElevationFileName || undefined);

    const [tab, setTab] = useState("active");

    const submit = () => {
        updater.callback({
            ...props.row,
            doorElevation: activeElevation,
            doorElevationFile: activeElevationFile,
            doorElevationFileName: activeElevationFileName,
            inactiveDoorElevation: inactiveElevation,
            inactiveDoorElevationFile: inactiveElevationFile,
            inactiveDoorElevationFileName: inactiveElevationFileName,
        })
    };

    return (
        <Dialog2 open onClose={props.onCancel} onEnterPress={submit} fullWidth maxWidth="lg">
            <DialogTitle>
                Split Elevations
            </DialogTitle>
            <DialogContent>
                <TabWrapper>
                    <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                        <Tab label="Active" value="active" />
                        <Tab label="In-Active" value="all" />
                    </Tabs>
                </TabWrapper>

                {tab === "active" ? <DoorElevationInner
                    key="active"
                    doorElevation={activeElevation} doorElevationFile={activeElevationFile}
                    doorElevationFileName={activeElevationFileName}
                    onUpdate={v => {
                        setActiveElevation(v.doorElevation);
                        setActiveElevationFile(v.doorElevationFile);
                        setActiveElevationFileName(v.doorElevationFileName);
                    }}
                /> : <DoorElevationInner
                    key="inactive"
                    doorElevation={inactiveElevation} doorElevationFile={inactiveElevationFile}
                    doorElevationFileName={inactiveElevationFileName}
                    onUpdate={v => {
                        setInactiveElevation(v.doorElevation);
                        setInactiveElevationFile(v.doorElevationFile);
                        setInactiveElevationFileName(v.doorElevationFileName);
                    }}
                />}
            </DialogContent>
            <DialogActions>
                <Button color="error" onClick={() => {
                    updater.callback({
                        ...props.row,
                        inactiveDoorElevation: "",
                        inactiveDoorElevationFile: undefined,
                        inactiveDoorElevationFileName: undefined,
                    })
                }}>Remove Split</Button>
                {updater.LoadingElement}
                <div style={{flex: 1}} />
                <Button onClick={() => props.onCancel()}>Cancel</Button>
                <Button variant="contained" onClick={submit}>Update</Button>
            </DialogActions>
        </Dialog2>
    )
}